import { useState, useEffect } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  BarChart3,
  Zap,
  Shield,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Mail,
  Camera,
  Gauge,
  Palette,
  Wrench,
  X,
  Check
} from 'lucide-react'
import { getCommonTrims } from '../lib/vinDecoder'

// Quick Edit Form Component
const QuickEditForm = ({ fieldKey, vehicle, value, onChange, onSave, onCancel, saving, saved }) => {
  const conditionOptions = [
    { value: 'concours', label: 'Concours / Show Quality', desc: 'Museum-quality or flawless original' },
    { value: 'excellent', label: 'Excellent', desc: 'Very well maintained, minimal wear' },
    { value: 'good', label: 'Good', desc: 'Presentable and mechanically sound' },
    { value: 'fair', label: 'Fair', desc: 'Runs and drives, shows significant wear' },
    { value: 'project', label: 'Project / Restoration', desc: 'Needs significant work' }
  ]

  const commonTrims = getCommonTrims(vehicle.make || '', vehicle.model || '')

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-3 mt-2 space-y-3 animate-fade-in">
      {fieldKey === 'mileage' && (
        <>
          <input
            type="number"
            min="0"
            max="999999"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter current mileage"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={saving || !value}
              className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {saved ? (
                <>
                  <Check size={14} />
                  Saved!
                </>
              ) : (
                'Save'
              )}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 btn-secondary text-sm py-2"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {fieldKey === 'color' && (
        <>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter exterior color"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={saving || !value}
              className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {saved ? (
                <>
                  <Check size={14} />
                  Saved!
                </>
              ) : (
                'Save'
              )}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 btn-secondary text-sm py-2"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {fieldKey === 'trim' && (
        <>
          <input
            type="text"
            list="trim-suggestions"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter trim level"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <datalist id="trim-suggestions">
            {commonTrims.map((trim) => (
              <option key={trim} value={trim} />
            ))}
          </datalist>
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={saving || !value}
              className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {saved ? (
                <>
                  <Check size={14} />
                  Saved!
                </>
              ) : (
                'Save'
              )}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 btn-secondary text-sm py-2"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {fieldKey === 'condition' && (
        <>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {conditionOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  value === option.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="condition"
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(e.target.value)}
                  className="mt-1 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                  <p className="text-xs text-gray-600">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={saving || !value}
              className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {saved ? (
                <>
                  <Check size={14} />
                  Saved!
                </>
              ) : (
                'Save'
              )}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 btn-secondary text-sm py-2"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const ValuationCard = ({ vehicle, marketValuation, onRefresh, loading = false, valuations = [], onUpdateClick, valuationCount = 0, limitReached = false, onRequestMore, onVehicleUpdate }) => {
  const [showComps, setShowComps] = useState(false)
  const [showDrivers, setShowDrivers] = useState(false)
  const [quickEdit, setQuickEdit] = useState(null)
  const [quickEditValue, setQuickEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedField, setSavedField] = useState(null)

  // Track fields that were just saved (so they disappear from the list)
  const [recentlySaved, setRecentlySaved] = useState([])

  // Check which vehicle info is missing
  const missingInfo = [
    { key: 'photo', missing: !vehicle.primary_image_url && (!vehicle.images || vehicle.images.length === 0), label: 'Add photos of your vehicle', icon: Camera },
    { key: 'mileage', missing: !vehicle.mileage && !vehicle.current_mileage, label: 'Update your current mileage', icon: Gauge },
    { key: 'color', missing: !vehicle.color, label: 'Add exterior color', icon: Palette },
    { key: 'trim', missing: !vehicle.trim, label: 'Specify your trim level', icon: ChevronDown },
    { key: 'condition', missing: !vehicle.condition, label: 'Set vehicle condition', icon: Shield }
  ]

  // Filter out recently saved fields even if vehicle prop hasn't re-rendered yet
  const visibleMissing = missingInfo.filter(i => i.missing && !recentlySaved.includes(i.key))
  const missingCount = visibleMissing.length
  const showCompleteness = missingCount >= 1 && !limitReached && marketValuation

  const mv = marketValuation

  // Confidence color
  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50'
    if (score >= 60) return 'text-amber-600 bg-amber-50'
    return 'text-red-600 bg-red-50'
  }

  // Trend icon and color
  const getTrendDisplay = (trend) => {
    switch (trend) {
      case 'appreciating':
        return { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', label: 'Appreciating' }
      case 'depreciating':
        return { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', label: 'Depreciating' }
      default:
        return { icon: Minus, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Stable' }
    }
  }

  // Impact indicator for value drivers
  const getImpactDisplay = (impact) => {
    switch (impact) {
      case 'positive':
        return { icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-50' }
      case 'negative':
        return { icon: ArrowDownRight, color: 'text-red-600', bg: 'bg-red-50' }
      default:
        return { icon: Minus, color: 'text-gray-500', bg: 'bg-gray-50' }
    }
  }

  // Source badge colors
  const getSourceColor = (source) => {
    const colors = {
      'Bring a Trailer': 'bg-orange-50 text-orange-700',
      'Cars & Bids': 'bg-blue-50 text-blue-700',
      'Mecum Auctions': 'bg-red-50 text-red-700',
      'RM Sotheby\'s': 'bg-purple-50 text-purple-700',
      'Bonhams': 'bg-emerald-50 text-emerald-700',
      'Gooding & Company': 'bg-indigo-50 text-indigo-700',
      'Barrett-Jackson': 'bg-amber-50 text-amber-700'
    }
    return colors[source] || 'bg-gray-50 text-gray-700'
  }

  // Format large currency values
  const formatValue = (val) => {
    if (!val) return '$0'
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  // Calculate position of mid value on the range bar
  const getRangePosition = () => {
    if (!mv) return 50
    const range = mv.estimated_high - mv.estimated_low
    if (range === 0) return 50
    return ((mv.estimated_mid - mv.estimated_low) / range) * 100
  }

  return (
    <div className="card p-6 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="section-title flex items-center gap-2">
          <BarChart3 size={20} />
          Market Valuation
        </h3>
        <div className="flex items-center gap-2">
          {onUpdateClick && (
            <button
              onClick={onUpdateClick}
              className="btn-secondary text-xs px-3 py-1.5"
            >
              Manual Entry
            </button>
          )}
          {limitReached ? (
            <Link
              to="/concierge"
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Mail size={14} />
              Talk to Concierge
            </Link>
          ) : (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Analyzing...' : 'Refresh'}
            </button>
          )}
        </div>
      </div>

      {mv ? (
        <>
          {/* Primary Value Display */}
          <div className="bg-gradient-to-br from-rdc-cream to-white rounded-xl p-6 mb-6 border border-rdc-warm-gray/30">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-medium text-rdc-taupe uppercase tracking-wide mb-1">Estimated Market Value</p>
                <span className="text-4xl font-bold text-rdc-primary">
                  {formatValue(mv.estimated_mid)}
                </span>
              </div>

              {/* Trend Badge */}
              {mv.market_trend && (() => {
                const trend = getTrendDisplay(mv.market_trend)
                const TrendIcon = trend.icon
                return (
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${trend.bg}`}>
                    <TrendIcon size={16} className={trend.color} />
                    <span className={`text-sm font-semibold ${trend.color}`}>
                      {trend.label}
                      {mv.trend_percentage ? ` ${mv.trend_percentage > 0 ? '+' : ''}${mv.trend_percentage}%` : ''}
                    </span>
                  </div>
                )
              })()}
            </div>

            {/* Value Range Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-rdc-taupe mb-2">
                <span>Low: {formatValue(mv.estimated_low)}</span>
                <span>High: {formatValue(mv.estimated_high)}</span>
              </div>
              <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                {/* Gradient fill */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-200 via-green-300 to-amber-200 rounded-full" />
                {/* Mid marker */}
                <div
                  className="absolute top-0 h-full w-1 bg-rdc-primary rounded-full shadow-sm"
                  style={{ left: `${getRangePosition()}%`, transform: 'translateX(-50%)' }}
                />
              </div>
            </div>

            {/* Meta Row */}
            <div className="flex items-center gap-4 text-xs text-rdc-taupe">
              {/* Confidence */}
              {mv.confidence_score && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${getConfidenceColor(mv.confidence_score)}`}>
                  <Shield size={12} />
                  <span className="font-medium">{mv.confidence_score}% confidence</span>
                </div>
              )}
              {/* Valuation count */}
              {valuationCount > 0 && (
                <span>
                  {valuationCount} of 3 valuations used
                </span>
              )}
              {/* Data Freshness */}
              {mv.data_freshness && (
                <span title={mv.data_freshness}>
                  Live market data
                </span>
              )}
              {/* Updated */}
              <span>
                Updated {formatDistanceToNow(new Date(mv.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>

          {/* Vehicle Completeness Prompts */}
          {showCompleteness && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Boost Your Valuation Accuracy</h4>
              <div className="space-y-2">
                {visibleMissing.map(item => {
                  const IconComponent = item.icon
                  const hasQuickEdit = onVehicleUpdate && ['mileage', 'color', 'trim', 'condition'].includes(item.key)

                  return (
                    <div key={item.key}>
                      {hasQuickEdit ? (
                        <button
                          onClick={() => {
                            setQuickEdit(item.key)
                            setQuickEditValue('')
                            setSavedField(null)
                          }}
                          className="w-full flex items-center gap-2 p-2.5 bg-white rounded hover:bg-blue-50 transition-colors group"
                        >
                          <IconComponent size={14} className="text-blue-600 flex-shrink-0" />
                          <span className="text-sm text-blue-900 group-hover:underline text-left">{item.label}</span>
                        </button>
                      ) : (
                        <Link
                          to={item.key === 'photo' ? `/garage/${vehicle.id}/edit` : `/garage/${vehicle.id}`}
                          className="flex items-center gap-2 p-2.5 bg-white rounded hover:bg-blue-50 transition-colors group"
                        >
                          <IconComponent size={14} className="text-blue-600 flex-shrink-0" />
                          <span className="text-sm text-blue-900 group-hover:underline">
                            {item.key === 'photo' ? 'Add photos (go to Edit Vehicle)' : item.label}
                          </span>
                        </Link>
                      )}

                      {/* Inline Quick Edit Forms */}
                      {quickEdit === item.key && hasQuickEdit && (
                        <QuickEditForm
                          fieldKey={item.key}
                          vehicle={vehicle}
                          value={quickEditValue}
                          onChange={setQuickEditValue}
                          onSave={async () => {
                            setSaving(true)
                            try {
                              const fieldMap = {
                                mileage: 'current_mileage',
                                color: 'color',
                                trim: 'trim',
                                condition: 'condition'
                              }
                              const fieldName = fieldMap[item.key]
                              const updates = { [fieldName]: quickEditValue }

                              await onVehicleUpdate(updates)

                              // Immediately hide this item from the list
                              setRecentlySaved(prev => [...prev, item.key])
                              setSavedField(item.key)
                              setQuickEdit(null)
                            } catch (err) {
                              console.error('Error saving:', err)
                              alert('Failed to save. Please try again.')
                            } finally {
                              setSaving(false)
                            }
                          }}
                          onCancel={() => {
                            setQuickEdit(null)
                            setSavedField(null)
                          }}
                          saving={saving}
                          saved={savedField === item.key}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Limit Reached — Concierge Handoff */}
          {limitReached && (
            <div className="bg-gradient-to-br from-rdc-cream to-white border border-rdc-warm-gray/30 rounded-lg p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-rdc-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-black mb-1">Want a deeper valuation?</p>
                  <p className="text-xs text-rdc-taupe mb-3">
                    Your 3 complimentary AI valuations are used. Our concierge team can connect you with professional appraisers, help you prepare your vehicle for sale, or arrange a formal valuation for insurance or estate purposes.
                  </p>
                  <Link
                    to="/concierge"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-rdc-primary text-white text-sm font-medium rounded-lg hover:bg-rdc-primary/90 transition-colors"
                  >
                    Talk to Our Concierge
                  </Link>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-rdc-warm-gray/20">
                <p className="text-xs font-medium text-rdc-taupe mb-2">Meanwhile, strengthen your vehicle's provenance:</p>
                <div className="space-y-1.5">
                  <Link to={`/garage/${vehicle.id}/edit`} className="flex items-center gap-2 p-2 bg-white/60 rounded hover:bg-white transition-colors text-xs text-rdc-taupe hover:text-black">
                    <Camera size={12} />
                    <span>Add photos and documentation</span>
                  </Link>
                  <Link to="/service-history/add" className="flex items-center gap-2 p-2 bg-white/60 rounded hover:bg-white transition-colors text-xs text-rdc-taupe hover:text-black">
                    <Wrench size={12} />
                    <span>Log service records to build your vehicle's history</span>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Summary */}
          {mv.analysis_summary && (
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <Zap size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 leading-relaxed">{mv.analysis_summary}</p>
              </div>
            </div>
          )}

          {/* Value Drivers */}
          {mv.value_drivers && mv.value_drivers.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowDrivers(!showDrivers)}
                className="flex items-center justify-between w-full py-3 text-sm font-medium text-black hover:text-rdc-primary transition-colors"
              >
                <span>Value Drivers ({mv.value_drivers.length})</span>
                {showDrivers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showDrivers && (
                <div className="space-y-2 pb-4">
                  {mv.value_drivers.map((driver, i) => {
                    const impact = getImpactDisplay(driver.impact)
                    const ImpactIcon = impact.icon
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-rdc-cream rounded-lg">
                        <div className={`w-7 h-7 rounded-full ${impact.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <ImpactIcon size={14} className={impact.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-black">{driver.factor}</p>
                          <p className="text-xs text-rdc-taupe leading-relaxed">{driver.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Comparable Sales */}
          {mv.comparable_sales && mv.comparable_sales.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowComps(!showComps)}
                className="flex items-center justify-between w-full py-3 text-sm font-medium text-black hover:text-rdc-primary transition-colors"
              >
                <span>Comparable Sales ({mv.comparable_sales.length})</span>
                {showComps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showComps && (
                <div className="space-y-2 pb-4">
                  {mv.comparable_sales.map((comp, i) => (
                    <div key={i} className="p-3 bg-rdc-cream rounded-lg">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black truncate">{comp.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSourceColor(comp.source)}`}>
                              {comp.source}
                            </span>
                            {comp.date && (
                              <span className="text-xs text-rdc-taupe">{comp.date}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-sm font-bold text-rdc-primary whitespace-nowrap">
                          {formatValue(comp.price)}
                        </span>
                      </div>
                      {comp.relevance && (
                        <p className="text-xs text-rdc-taupe mt-1.5 leading-relaxed">{comp.relevance}</p>
                      )}
                      {comp.url && (
                        <a
                          href={comp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-rdc-primary hover:underline mt-1"
                        >
                          View listing <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Legacy valuations (user-entered) */}
          {valuations && valuations.length > 0 && (
            <div className="border-t border-rdc-warm-gray/30 pt-4 mt-2">
              <p className="text-xs font-medium text-rdc-taupe uppercase tracking-wide mb-3">Your Recorded Valuations</p>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {[...valuations].slice(0, 3).map((v) => (
                  <div key={v.id} className="flex items-center justify-between p-2 bg-rdc-cream rounded-lg">
                    <div>
                      <p className="text-xs text-rdc-taupe">
                        {v.source === 'personal_estimate' ? 'Personal' : v.source}
                        {' · '}
                        {v.valuation_date ? format(new Date(v.valuation_date), 'MMM d, yyyy') : ''}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-rdc-dark-gray">
                      {formatValue(v.estimated_value || v.value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* No AI valuation yet - prompt to generate one */
        <div className="bg-gradient-to-br from-rdc-cream to-white rounded-xl p-8 text-center border border-rdc-warm-gray/30">
          <BarChart3 size={48} className="text-rdc-warm-gray mx-auto mb-3" />
          <h4 className="text-lg font-semibold text-black mb-2">AI Market Valuation</h4>
          <p className="text-sm text-rdc-taupe mb-4 max-w-sm mx-auto">
            Get an instant market estimate based on recent auction results from Bring a Trailer, Cars & Bids, Mecum, and more.
          </p>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="btn-primary inline-flex items-center gap-2 mb-3"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Analyzing market data...
              </>
            ) : (
              <>
                <Zap size={16} />
                Generate Valuation
              </>
            )}
          </button>
          <p className="text-xs text-rdc-taupe">3 complimentary valuations per vehicle</p>
        </div>
      )}
    </div>
  )
}

export default ValuationCard
